import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const COLORS = ['#ff0000', '#ffff00', '#00cc00', '#ffffff', '#000000'];

const toolBtnStyle = (active) => ({
  border: 'none', background: active ? '#1d1d1f' : 'none', width: 34, height: 34,
  borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
});
const iconBtnStyle = { border: 'none', background: 'none', width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const dividerStyle = { width: 1, height: 24, background: '#e5e5ea' };

function drawAnnotation(ctx, ann, w, h) {
  ctx.save();
  ctx.strokeStyle = ann.color;
  ctx.fillStyle = ann.color;
  ctx.lineWidth = ann.strokeWidth || 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (ann.type === 'circle') {
    ctx.beginPath();
    ctx.arc(ann.cx * w, ann.cy * h, ann.r * Math.min(w, h), 0, 2 * Math.PI);
    ctx.stroke();
  } else if (ann.type === 'text') {
    const fontSize = Math.round((ann.fontSize || 0.05) * Math.min(w, h));
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.fillStyle = ann.color;
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = fontSize / 10;
    ctx.strokeText(ann.text, ann.x * w, ann.y * h);
    ctx.fillText(ann.text, ann.x * w, ann.y * h);
  } else if (ann.type === 'arrow') {
    const x1 = ann.x1 * w, y1 = ann.y1 * h;
    const x2 = ann.x2 * w, y2 = ann.y2 * h;
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 5) { ctx.restore(); return; }
    const headLen = Math.min(22, len * 0.35);
    const angle = Math.atan2(dy, dx);

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

export default function AnnotationModal({ image, imageUrl, onSave, onClose, fetchSasUrlWithCache }) {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('circle');
  const [color, setColor] = useState('#ff0000');
  const [annotations, setAnnotations] = useState(() => image.metadata?.annotations || []);
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 });
  const drawingRef = useRef(false);
  const startPosRef = useRef(null);
  const [textInput, setTextInput] = useState(null); // { normX, normY, screenX, screenY }
  const textInputRef = useRef();
  // Guards the self-healing retry below against looping — one retry is
  // enough to recover from the common case (this blob: URL was evicted
  // from the shared in-memory LRU cache, or a corrupted raw blob persisted
  // in IndexedDB — forceRefetch bypasses both and pulls fresh from network).
  const retriedRef = useRef(false);
  const retryImage = () => {
    if (retriedRef.current) return;
    retriedRef.current = true;
    fetchSasUrlWithCache?.(image, true);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  };

  const redraw = useCallback((anns, preview = null) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    anns.forEach(ann => drawAnnotation(ctx, ann, canvas.width, canvas.height));
    if (preview) drawAnnotation(ctx, preview, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const maxW = Math.min(window.innerWidth - 80, 900);
      const maxH = window.innerHeight - 180;
      const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
      setCanvasSize({
        w: Math.round(img.naturalWidth * scale),
        h: Math.round(img.naturalHeight * scale),
      });
    };
    // Previously had no error handling at all — a stale/evicted blob: URL
    // (this exact report has 226 images against a 150-entry shared cache)
    // failed silently here, leaving canvasSize stuck at its {800,600}
    // default and the real <img> below also broken, since both read the
    // same imageUrl. Retry once via the same re-fetch path the grid uses.
    img.onerror = retryImage;
    img.src = imageUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  useEffect(() => {
    redraw(annotations);
  }, [annotations, redraw, canvasSize]);

  const onMouseDown = (e) => {
    if (tool === 'text') return;
    e.preventDefault();
    drawingRef.current = true;
    startPosRef.current = getPos(e);
  };

  const onMouseMove = (e) => {
    if (!drawingRef.current || !startPosRef.current) return;
    e.preventDefault();
    const cur = getPos(e);
    const { x: sx, y: sy } = startPosRef.current;
    let preview;
    if (tool === 'circle') {
      const dx = cur.x - sx, dy = cur.y - sy;
      preview = { type: 'circle', cx: sx, cy: sy, r: Math.sqrt(dx * dx + dy * dy), color, strokeWidth: 3 };
    } else {
      preview = { type: 'arrow', x1: sx, y1: sy, x2: cur.x, y2: cur.y, color, strokeWidth: 3 };
    }
    redraw(annotations, preview);
  };

  const onMouseUp = (e) => {
    if (tool === 'text') {
      e.preventDefault();
      const pos = getPos(e);
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const clientX = e.touches ? e.changedTouches[0].clientX : e.clientX;
      const clientY = e.touches ? e.changedTouches[0].clientY : e.clientY;
      setTextInput({ normX: pos.x, normY: pos.y, screenX: clientX - (rect?.left || 0), screenY: clientY - (rect?.top || 0) });
      setTimeout(() => textInputRef.current?.focus(), 50);
      return;
    }
    if (!drawingRef.current || !startPosRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    const cur = getPos(e);
    const { x: sx, y: sy } = startPosRef.current;
    startPosRef.current = null;

    let ann;
    if (tool === 'circle') {
      const dx = cur.x - sx, dy = cur.y - sy;
      const r = Math.sqrt(dx * dx + dy * dy);
      if (r < 0.01) return;
      ann = { type: 'circle', cx: sx, cy: sy, r, color, strokeWidth: 3 };
    } else {
      const dx = cur.x - sx, dy = cur.y - sy;
      if (Math.sqrt(dx * dx + dy * dy) < 0.02) return;
      ann = { type: 'arrow', x1: sx, y1: sy, x2: cur.x, y2: cur.y, color, strokeWidth: 3 };
    }
    setAnnotations(prev => [...prev, ann]);
  };

  const confirmText = (text) => {
    if (text.trim() && textInput) {
      setAnnotations(prev => [...prev, { type: 'text', x: textInput.normX, y: textInput.normY, text: text.trim(), color, fontSize: 0.05 }]);
    }
    setTextInput(null);
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', zIndex: 9999, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Toolbar — monochrome icon buttons, active tool as a filled dark
          pill, refined swatches, one accent-green Save + a plain text
          Cancel, instead of default Bootstrap btn-primary/outline chrome. */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '8px 12px',
        marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center',
        flexWrap: 'wrap', boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
      }}>
        <button
          type="button"
          style={toolBtnStyle(tool === 'circle')}
          onClick={() => setTool('circle')}
          title="Ympyrä"
          aria-label="Ympyrä"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={tool === 'circle' ? '#fff' : '#6e6e73'} strokeWidth="2"><circle cx="12" cy="12" r="8" /></svg>
        </button>
        <button
          type="button"
          style={toolBtnStyle(tool === 'arrow')}
          onClick={() => setTool('arrow')}
          title="Nuoli"
          aria-label="Nuoli"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={tool === 'arrow' ? '#fff' : '#6e6e73'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7" /><path d="M9 7h8v8" /></svg>
        </button>
        <button
          type="button"
          style={toolBtnStyle(tool === 'text')}
          onClick={() => setTool('text')}
          title="Teksti"
          aria-label="Teksti"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={tool === 'text' ? '#fff' : '#6e6e73'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 7V5h14v2" /><path d="M12 5v14" /><path d="M9 19h6" /></svg>
        </button>

        <div style={dividerStyle} />

        {COLORS.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            title={c}
            aria-label={`Väri ${c}`}
            style={{
              width: 22, height: 22, borderRadius: '50%', background: c,
              border: color === c ? '1px solid #d8d8dc' : '1px solid #e5e5ea',
              outline: color === c ? '2px solid #1d1d1f' : 'none', outlineOffset: 2,
              cursor: 'pointer', padding: 0, flexShrink: 0,
            }}
          />
        ))}

        <div style={dividerStyle} />

        <button
          type="button"
          style={iconBtnStyle}
          onClick={() => setAnnotations(prev => prev.slice(0, -1))}
          title="Kumoa"
          aria-label="Kumoa"
          disabled={annotations.length === 0}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={annotations.length === 0 ? '#c7c7cc' : '#6e6e73'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 1 3 6.7" /><path d="M3 17v-5h5" /></svg>
        </button>

        {annotations.length > 0 && (
          <button
            type="button"
            style={iconBtnStyle}
            onClick={() => setAnnotations([])}
            title="Poista kaikki"
            aria-label="Poista kaikki"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b42318" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /><path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" /></svg>
          </button>
        )}

        <div style={dividerStyle} />

        <button
          type="button"
          style={{ border: 'none', background: '#1d7a46', color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
          onClick={() => onSave(annotations)}
        >
          Tallenna
        </button>
        <button
          type="button"
          style={{ border: 'none', background: 'none', color: '#6e6e73', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '8px 8px' }}
          onClick={onClose}
        >
          Peruuta
        </button>
      </div>

      {/* Canvas area */}
      <div style={{ position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.5)', flexShrink: 0 }}>
        <img
          src={imageUrl}
          alt=""
          draggable={false}
          onError={retryImage}
          style={{
            display: 'block',
            width: canvasSize.w,
            height: canvasSize.h,
            objectFit: 'contain',
            userSelect: 'none',
          }}
        />
        <canvas
          ref={canvasRef}
          width={canvasSize.w}
          height={canvasSize.h}
          style={{
            position: 'absolute', top: 0, left: 0,
            cursor: tool === 'text' ? 'text' : tool === 'circle' ? 'crosshair' : 'default',
            touchAction: 'none',
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={e => { if (tool !== 'text') onMouseUp(e); }}
          onTouchStart={onMouseDown}
          onTouchMove={onMouseMove}
          onTouchEnd={onMouseUp}
        />
        {textInput && (
          <input
            ref={textInputRef}
            autoFocus
            type="text"
            placeholder="Kirjoita teksti..."
            style={{
              position: 'absolute',
              left: textInput.screenX,
              top: textInput.screenY,
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: color,
              border: `2px solid ${color}`,
              borderRadius: 4,
              padding: '3px 8px',
              fontSize: 16,
              fontWeight: 'bold',
              outline: 'none',
              minWidth: 120,
              zIndex: 10,
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') confirmText(e.target.value);
              if (e.key === 'Escape') setTextInput(null);
            }}
            onBlur={e => confirmText(e.target.value)}
          />
        )}
      </div>

      <div style={{ color: '#aaa', fontSize: 12, marginTop: 8 }}>
        {tool === 'circle' ? 'Vedä piirtääksesi ympyrä' : tool === 'arrow' ? 'Vedä piirtääksesi nuoli' : 'Klikkaa lisätäksesi teksti'} • Klikkaa taustaa sulkeaksesi
      </div>
    </div>,
    document.body
  );
}
